/**
 * Email Distribution Service (US-041 Phase 1-2)
 * Handles email sending, tracking, and analytics for newsletter distribution
 *
 * Key Features:
 * - Send individual emails via AWS SES
 * - Send batch email campaigns
 * - Track email events (opens, clicks, bounces, replies)
 * - Manage email messages and campaigns
 * - Support email threading for conversations
 *
 * Dependencies:
 * - AWS SES (via Lambda function - to be implemented)
 * - GraphQL (EmailMessage, EmailCampaign, EmailEvent models)
 */

import { generateClient } from 'aws-amplify/api';
import logger from '../utils/logger.js';
import auditService from './AuditService.js';
// Phase 1B (#223) — wire UserBranding.primaryColor into outgoing HTML.
// Helper lives next door so we don't duplicate the color sanitiser.
import { applyBrandingToHtml } from './NewsletterAIService.js';
// #368 — the ONE email-safe wrapper (600px table + preheader) for all sends.
import { buildEmailShell, htmlToPlainText } from '../utils/emailShell.js';
import { htmlToEmailText } from '../utils/htmlTextBridge.js';
import { resolveReplyTo } from '../utils/replyEmail.js';


// #471: profile and override strings arrive with stray whitespace
// ("Ashish " shipped as "Ashish  Markanday"); every name-ish value passes
// through here before it reaches a header, a subject or the footer.
const cleanName = (value) => (value == null ? '' : String(value).replace(/\s+/g, ' ').trim());

// #471: the text part renders links as "label (url)"; the inbox preview
// (preheader) keeps the caller's curated text (#368) minus those URLs.
const stripInlineLinkUrls = (text) =>
  (text || '').replace(/\s?\((?:https?:\/\/|mailto:)[^)\s]+\)/g, '');

class EmailService {
  constructor() {
    // Create service-specific logger
    this.logger = logger.child('EmailService');

    // Initialize Amplify client
    this.client = generateClient();

    this.sesEndpoint = null; // Will be set to Lambda function URL
    this.trackingDomain = null; // Will be set for open/click tracking
  }

  /**
   * Initialize email service with configuration
   * @param {Object} config
   * @param {string} config.sesEndpoint - AWS SES Lambda function URL
   * @param {string} config.trackingDomain - Domain for tracking pixel/links
   */
  initialize(config = {}) {
    this.sesEndpoint = config.sesEndpoint || import.meta.env.VITE_SES_LAMBDA_URL;
    this.trackingDomain = config.trackingDomain || import.meta.env.VITE_EMAIL_TRACKING_DOMAIN;
    this.unsubscribeUrl = config.unsubscribeUrl || import.meta.env.VITE_UNSUBSCRIBE_LAMBDA_URL;
    this.logger.info('Initialized with config:', {
      sesEndpoint: this.sesEndpoint ? 'configured' : 'not configured',
      trackingDomain: this.trackingDomain ? 'configured' : 'not configured',
      unsubscribeUrl: this.unsubscribeUrl ? 'configured' : 'not configured'
    });
  }

  /**
   * Unsubscribe endpoint base URL (#362).
   * MUST come from configuration — never window.location.origin, which leaks
   * dev/preview hosts (dead localhost links) into client inboxes.
   * @private
   */
  getUnsubscribeBaseUrl() {
    const configured = this.unsubscribeUrl || import.meta.env.VITE_UNSUBSCRIBE_LAMBDA_URL;
    if (configured) return configured;

    // Rule 15: a silent hardcoded fallback would ship DEAD unsubscribe
    // links (CAN-SPAM failure) whenever the env var is missing. Fall back
    // to the public site, but shout about it on every send until fixed —
    // #386 tracks failing deploys on this class of missing config.
    if (!this._warnedUnsubscribeConfig) {
      this._warnedUnsubscribeConfig = true;
      this.logger.error(
        'VITE_UNSUBSCRIBE_LAMBDA_URL is not configured — unsubscribe links ' +
        'will not resolve to the unsubscribe Lambda. See ' +
        'docs/03-technical/RUNBOOK-send-path-hardening.md §1.'
      );
    }
    const site = import.meta.env.VITE_PUBLIC_SITE_URL || 'https://finnewsy.com';
    return `${site}/unsubscribe`;
  }

  /**
   * Resolve the advisor's profile (firm name, postal address, reply email)
   * for the CAN-SPAM footer and sender identity. Cached per userId; a lookup
   * failure must never block a send — it just degrades the footer.
   * @private
   */
  async getAdvisorProfile(userId) {
    if (!userId) return null;
    if (!this._advisorProfileCache) this._advisorProfileCache = new Map();
    if (this._advisorProfileCache.has(userId)) {
      return this._advisorProfileCache.get(userId);
    }
    try {
      const { default: profileService } = await import('./ProfileService.js');
      const profile = await profileService.getProfileByUserId(userId);
      this._advisorProfileCache.set(userId, profile || null);
      return profile || null;
    } catch (error) {
      // Review fix: do NOT cache lookup FAILURES — one transient blip used
      // to pin null for the whole session, silently degrading the CAN-SPAM
      // footer and sender identity on every subsequent send.
      this.logger.warn('Advisor profile lookup failed (footer will degrade):', error?.message);
      return null;
    }
  }

  /**
   * Firm-name resolution used by BOTH the sender identity (fromName) and
   * the CAN-SPAM footer — one chain so the two can never disagree.
   * @private
   */
  resolveFirmName(profile) {
    return (
      cleanName(profile?.firmName) ||
      cleanName(profile?.company) ||
      cleanName(profile?.communicationStyle?.firmName) ||
      null
    );
  }

  /**
   * Send newsletter to a single client
   * @param {Object} options
   * @param {string} options.newsletterId - Newsletter to send
   * @param {string} options.clientId - Client recipient
   * @param {string} options.userId - Current user (advisor)
   * @param {string} options.subject - Email subject line
   * @param {string} options.bodyHtml - HTML email body
   * @param {string} options.bodyText - Plain text email body
   * @param {Object} options.personalization - Personalization data for placeholders
   * @returns {Object} Send result with tracking information
   */
  /**
   * Phase 1B (#223) — wrap outgoing HTML with the advisor's
   * UserBranding.primaryColor before send. Gated on VITE_USE_NICHE_FRAMING.
   * Identity passthrough on failure so a flaky branding lookup never
   * breaks email send.
   */
  async _applyOutgoingBrandingIfFlagged(html, userId) {
    if (!html || !userId) return html;
    if (import.meta.env?.VITE_USE_NICHE_FRAMING !== 'true') return html;
    try {
      const wrapped = await applyBrandingToHtml({ html, userId });
      // #105 — observability: emit structured event ONLY when the helper
      // actually wrapped the HTML (advisor has a valid primary color set).
      // No-op paths (no branding row / invalid color) don't fire so the
      // engagement-rate metric reflects active brand-color usage.
      if (wrapped !== html) {
        this.logger?.info?.('phase1b.outgoing_branding.engaged', { userId });
      }
      return wrapped;
    } catch (err) {
      // #105 — observability: structured failure event for the dashboard.
      this.logger?.warn?.('phase1b.outgoing_branding.failed', {
        userId,
        reason: err?.message || String(err),
      });
      return html;
    }
  }

  async sendNewsletter({ newsletterId, clientId, userId, subject, bodyHtml, bodyText, campaignId, personalization = {}, replyTo, fromNameOverride, enableTracking = true }) {
    try {
      this.logger.info(`Sending newsletter ${newsletterId} to client ${clientId}`);

      // 1. Get client details
      const client = await this.getClient(clientId);
      if (!client) {
        throw new Error(`Client ${clientId} not found`);
      }

      if (!client.email) {
        throw new Error(`Client ${clientId} has no email address`);
      }

      // CAN-SPAM (#362): recorded opt-outs are honored on EVERY send path.
      // The unsubscribe Lambda sets `unsubscribed` on the Client record; a
      // suppressed recipient gets no message row and no Lambda call.
      if (client.unsubscribed) {
        this.logger.info(`Suppressing send to unsubscribed client ${clientId}`);
        return {
          success: false,
          suppressed: true,
          error: 'Recipient has unsubscribed',
          client: {
            id: client.id,
            email: client.email,
            name: `${client.firstName} ${client.lastName}`
          }
        };
      }

      // Advisor identity: CAN-SPAM footer (firm name + postal address) and
      // sender presentation (fromName + Reply-To, #369)
      const advisorProfile = await this.getAdvisorProfile(userId);
      const senderIdentity = await this.getSenderIdentity(userId, advisorProfile);
      // #391: honor the wizard's Step-4 sender overrides. The From address
      // stays the SES-verified sender; the advisor's typed fromName / reply-to
      // (when provided) win over the profile-derived defaults.
      if (cleanName(fromNameOverride)) {
        senderIdentity.fromName = cleanName(fromNameOverride);
      }
      if (replyTo && replyTo.trim()) {
        // #433: an override is honoured only when usable (platform domain or
        // the advisor's SES-verified address); otherwise keep the profile-
        // derived reply-to rather than shipping an unverified one.
        const resolved = resolveReplyTo({ override: replyTo, profile: advisorProfile });
        if (resolved !== replyTo.trim().toLowerCase()) {
          this.logger.warn(`Reply-To override ${replyTo.trim()} is not verified; using ${resolved || 'none'}`);
        }
        senderIdentity.replyTo = resolved;
      }

      // 2. Personalize content
      const personalizedSubject = this.personalizeContent(subject, {
        ...personalization,
        client_name: `${client.firstName} ${client.lastName}`,
        first_name: client.firstName
      });

      let personalizedHtml = this.personalizeContent(bodyHtml, {
        ...personalization,
        client_name: `${client.firstName} ${client.lastName}`,
        first_name: client.firstName
      });

      // Phase 1B (#223 / #129) — wrap outgoing HTML with advisor brand colour.
      // No-op when VITE_USE_NICHE_FRAMING is off.
      personalizedHtml = await this._applyOutgoingBrandingIfFlagged(personalizedHtml, userId);

      const personalizedText = this.personalizeContent(bodyText, {
        ...personalization,
        client_name: `${client.firstName} ${client.lastName}`,
        first_name: client.firstName
      });

      // 3. Assemble the shippable document (#368):
      //    content + CAN-SPAM footer → shared email-safe shell (600px table,
      //    hidden preheader). Tracking is added AFTER shelling so the pixel
      //    lands before a real </body> and link-wrapping sees the final doc.
      const contentWithFooter = this.addUnsubscribeLink(personalizedHtml, {
        clientId,
        newsletterId,
        advisorProfile
      });

      // Review fix (#362): the text/plain part previously shipped WITHOUT
      // the unsubscribe link + postal-address footer — recipients with HTML
      // blocked got a commercial email with no opt-out mechanism.
      // #471: the email renderer keeps URLs, so the text part's Unsubscribe
      // link is actionable for readers with HTML off.
      const footerText = htmlToEmailText(
        this.buildFooterHtml({ clientId, newsletterId, advisorProfile })
      );
      const bodyTextWithFooter = personalizedText && personalizedText.trim()
        ? `${personalizedText.trim()}\n\n${footerText}`
        : `${htmlToEmailText(personalizedHtml)}\n\n${footerText}`;

      const preheaderSource = stripInlineLinkUrls(personalizedText) || htmlToPlainText(personalizedHtml);
      const shelledHtml = buildEmailShell({
        bodyHtml: contentWithFooter,
        preheader: (preheaderSource || '').replace(/\s+/g, ' ').trim().slice(0, 120),
        title: personalizedSubject
      });

      // 4. Create EmailMessage record (status: QUEUED)
      // We need the emailMessageId before adding tracking pixel
      const emailMessage = await this.createEmailMessage({
        userId,
        clientId,
        newsletterId,
        campaignId,
        direction: 'OUTBOUND',
        subject: personalizedSubject,
        from: senderIdentity.from,
        to: [client.email],
        bodyHtml: shelledHtml, // Temporarily store shelled HTML without tracking
        bodyText: personalizedText,
        status: 'QUEUED'
      });

      // 5. Now add tracking pixel with emailMessageId (Lambda expects ?id=emailMessageId)
      // #391: honor the Step-4 tracking toggle — previously the pixel was
      // added unconditionally regardless of the advisor's choice.
      const finalHtml = enableTracking
        ? this.addTracking(shelledHtml, {
            emailMessageId: emailMessage.id,
            clientId,
            newsletterId
          })
        : shelledHtml;

      // 6. Update EmailMessage with final HTML (now includes tracking pixel)
      await this.updateEmailMessage(emailMessage.id, {
        bodyHtml: finalHtml
      });

      // 7. Send via AWS SES (Lambda function) with retry logic.
      // clientId rides along so the Lambda can token-sign the unsubscribe
      // link, set List-Unsubscribe headers, and enforce suppression
      // server-side (#362/#363).
      const sendResult = await this.sendWithRetry({
        messageId: emailMessage.id,
        to: client.email,
        subject: personalizedSubject,
        bodyHtml: finalHtml,
        bodyText: bodyTextWithFooter,
        from: emailMessage.from,
        fromName: senderIdentity.fromName,
        replyTo: senderIdentity.replyTo,
        clientId,
        campaignId,
        enableTracking
      }, 3); // Max 3 retries

      // 8. Update EmailMessage with send status
      await this.updateEmailMessage(emailMessage.id, {
        status: sendResult.success ? 'SENT' : 'FAILED',
        sentAt: sendResult.success ? new Date().toISOString() : null,
        sesMessageId: sendResult.sesMessageId,
        sesSendRequestId: sendResult.requestId,
        errorMessage: sendResult.error || null,
        sendAttempts: 1,
        lastSendAttempt: new Date().toISOString()
      });

      // 9. Record SENT event
      if (sendResult.success) {
        await this.recordEmailEvent({
          userId,
          emailMessageId: emailMessage.id,
          clientId,
          campaignId,
          eventType: 'SENT',
          timestamp: new Date().toISOString()
        });
      }

      // Compliance audit trail
      auditService.logAction({
        action: 'NEWSLETTER_SENT',
        resourceType: 'NEWSLETTER',
        resourceId: newsletterId || campaignId,
        content: bodyHtml,
        details: {
          clientId,
          emailMessageId: emailMessage.id,
          success: sendResult.success,
          sesMessageId: sendResult.sesMessageId,
        },
      }).catch(() => {}); // Non-blocking

      return {
        success: sendResult.success,
        // Server-side suppression (stale frontend data) classifies as
        // suppressed, not failed — same contract as the client-side check.
        ...(sendResult.suppressed ? { suppressed: true } : {}),
        emailMessageId: emailMessage.id,
        sesMessageId: sendResult.sesMessageId,
        client: {
          id: client.id,
          email: client.email,
          name: `${client.firstName} ${client.lastName}`
        },
        error: sendResult.error
      };

    } catch (error) {
      this.logger.error('Error sending newsletter:', error);
      throw error;
    }
  }

  /**
   * Send newsletter to multiple clients (batch campaign)
   * @param {Object} options
   * @param {string} options.newsletterId - Newsletter to send
   * @param {string[]} options.clientIds - Array of client IDs
   * @param {string} options.userId - Current user (advisor)
   * @param {string} options.campaignName - Campaign name
   * @param {string} options.subject - Email subject line
   * @param {string} options.bodyHtml - HTML email body
   * @param {string} options.bodyText - Plain text email body
   * @param {Date} options.scheduledFor - Optional schedule time
   * @returns {Object} Campaign creation result
   */
  /**
   * #434 — Send the advisor a rendered test copy of a newsletter.
   *
   * Renders exactly what a client would receive (personalisation against the
   * advisor as the sample recipient, outgoing branding, footer, email shell)
   * but is NOT a distribution: no EmailMessage row, no tracking pixel, no
   * clientId/campaignId (so the sender Lambda skips suppression, unsubscribe
   * tokens and SENT events), and the subject is prefixed "[TEST] ".
   *
   * The caller is responsible for running the pre-send guard first (the
   * disclaimer arrives already injected in bodyHtml) and for auditing.
   */
  async sendTestNewsletter({ userId, to, subject, bodyHtml, bodyText, replyTo, fromNameOverride }) {
    const destination = typeof to === 'string' ? to.trim() : '';
    if (!destination) {
      throw new Error('A destination email address is required for a test send');
    }

    const advisorProfile = await this.getAdvisorProfile(userId);
    const senderIdentity = await this.getSenderIdentity(userId, advisorProfile);
    if (cleanName(fromNameOverride)) {
      senderIdentity.fromName = cleanName(fromNameOverride);
    }
    if (replyTo && replyTo.trim()) {
      senderIdentity.replyTo = replyTo.trim();
    }

    const advisorName = [advisorProfile?.firstName, advisorProfile?.lastName].map(cleanName).filter(Boolean).join(' ') || 'Advisor';
    const sample = { client_name: advisorName, first_name: cleanName(advisorProfile?.firstName) || advisorName };

    const testSubject = `[TEST] ${this.personalizeContent(subject || '', sample)}`;
    let personalizedHtml = this.personalizeContent(bodyHtml || '', sample);
    personalizedHtml = await this._applyOutgoingBrandingIfFlagged(personalizedHtml, userId);
    const personalizedText = this.personalizeContent(bodyText || '', sample);

    // Footer as clients see it; the unsubscribe link carries a placeholder id
    // so the advisor sees the layout without an actionable opt-out.
    const footerCtx = { clientId: 'test-send', newsletterId: null, advisorProfile };
    const contentWithFooter = this.addUnsubscribeLink(personalizedHtml, footerCtx);
    const footerText = htmlToEmailText(this.buildFooterHtml(footerCtx));
    const textWithFooter = personalizedText && personalizedText.trim()
      ? `${personalizedText.trim()}\n\n${footerText}`
      : `${htmlToEmailText(personalizedHtml)}\n\n${footerText}`;

    const preheaderSource = stripInlineLinkUrls(personalizedText) || htmlToPlainText(personalizedHtml);
    const shelledHtml = buildEmailShell({
      bodyHtml: contentWithFooter,
      preheader: (preheaderSource || '').replace(/\s+/g, ' ').trim().slice(0, 120),
      title: testSubject
    });

    const result = await this.sendViaSES({
      messageId: null, // no EmailMessage row — sender skips pixel + SENT event
      to: [destination],
      subject: testSubject,
      bodyHtml: shelledHtml,
      bodyText: textWithFooter,
      from: senderIdentity.from,
      fromName: senderIdentity.fromName,
      replyTo: senderIdentity.replyTo
    });

    if (!result?.success) {
      return { success: false, error: result?.error || 'Test send failed' };
    }
    return { success: true, messageId: result.messageId, to: destination, subject: testSubject };
  }

  async sendCampaign({
    newsletterId,
    clientIds: rawClientIds,
    recipientIds,
    userId,
    campaignName: rawCampaignName,
    name,
    subject,
    bodyHtml,
    bodyText,
    scheduledFor = null
  }) {
    const clientIds = rawClientIds || recipientIds;
    const campaignName = rawCampaignName || name;
    try {
      this.logger.info(`Creating campaign "${campaignName}" for ${clientIds.length} clients`);

      // 1. Create EmailCampaign record
      const campaign = await this.createEmailCampaign({
        userId,
        newsletterId,
        name: campaignName,
        subject,
        recipientClientIds: clientIds,
        recipientCount: clientIds.length,
        status: scheduledFor ? 'SCHEDULED' : 'SENDING',
        scheduledFor: scheduledFor ? scheduledFor.toISOString() : null,
        startedAt: !scheduledFor ? new Date().toISOString() : null,
        contentSnapshot: { bodyHtml, bodyText } // Store for historical analysis
      });

      this.logger.info(`Campaign ${campaign.id} created, status: ${campaign.status}`);

      // 2. If not scheduled, send immediately
      let processResults = { sent: 0, failed: 0, errors: [] };
      if (!scheduledFor) {
        processResults = await this.processCampaign(campaign.id, {
          newsletterId,
          clientIds,
          userId,
          subject,
          bodyHtml,
          bodyText
        });
      }

      return {
        success: true,
        sent: processResults.sent,
        failed: processResults.failed,
        failures: processResults.errors,
        campaign: {
          id: campaign.id,
          name: campaign.name,
          recipientCount: campaign.recipientCount,
          status: campaign.status,
          scheduledFor: campaign.scheduledFor
        }
      };

    } catch (error) {
      this.logger.error('Error creating campaign:', error);
      throw error;
    }
  }

  /**
   * #407: read the Step-4 sender overrides persisted in a campaign's metadata
   * (fromName / replyToEmail / enableTracking, written by the wizard #391) so
   * batch/scheduled sends honor them like the immediate per-client loop does.
   * @private
   */
  async _loadSenderOverrides(campaignId) {
    try {
      const { data: campaign } = await this.client.models.EmailCampaign.get({ id: campaignId });
      let meta = campaign?.metadata;
      if (typeof meta === 'string') {
        try { meta = JSON.parse(meta); } catch { meta = null; }
      }
      meta = meta || {};
      return {
        fromNameOverride: meta.fromName || undefined,
        replyTo: meta.replyToEmail || undefined,
        enableTracking: meta.enableTracking !== undefined ? meta.enableTracking : true
      };
    } catch (err) {
      this.logger.warn(`_loadSenderOverrides: could not read campaign ${campaignId} metadata`, err);
      return { fromNameOverride: undefined, replyTo: undefined, enableTracking: true };
    }
  }

  /**
   * Process a campaign (send to all recipients)
   * @private
   */
  async processCampaign(campaignId, { newsletterId, clientIds, userId, subject, bodyHtml, bodyText }) {
    try {
      this.logger.info(`Processing campaign ${campaignId} for ${clientIds.length} recipients`);

      // #407: honor the Step-4 sender overrides (fromName / reply-to / tracking)
      // on this batch/scheduled path too — they're persisted in the campaign's
      // metadata by the wizard (#391). Parse once, thread into every send.
      const senderOverrides = await this._loadSenderOverrides(campaignId);

      const results = {
        sent: 0,
        failed: 0,
        suppressed: 0, // CAN-SPAM (#362): unsubscribed recipients are skipped, not "failed"
        errors: []
      };

      // Send to each client with rate limiting (14 emails/sec AWS SES limit)
      for (let i = 0; i < clientIds.length; i++) {
        const clientId = clientIds[i];

        try {
          const result = await this.sendNewsletter({
            newsletterId,
            clientId,
            userId,
            subject,
            bodyHtml,
            bodyText,
            campaignId,
            // #407: Step-4 sender overrides from the campaign metadata.
            fromNameOverride: senderOverrides.fromNameOverride,
            replyTo: senderOverrides.replyTo,
            enableTracking: senderOverrides.enableTracking
          });

          if (result.success) {
            results.sent++;
          } else if (result.suppressed) {
            results.suppressed++;
          } else {
            results.failed++;
            results.errors.push({
              clientId,
              error: result.error,
              timestamp: new Date().toISOString()
            });
          }

          // Rate limiting: 14 emails/sec = ~71ms between emails
          if (i < clientIds.length - 1) {
            await this.delay(75);
          }

        } catch (error) {
          results.failed++;
          results.errors.push({
            clientId,
            error: error.message,
            timestamp: new Date().toISOString()
          });
        }

        // Update campaign progress every 10 emails
        if (i % 10 === 0 || i === clientIds.length - 1) {
          await this.updateCampaignProgress(campaignId, {
            sentCount: results.sent,
            failedCount: results.failed,
            processingErrors: results.errors
          });
        }
      }

      // Mark campaign as completed
      await this.updateEmailCampaign(campaignId, {
        status: 'COMPLETED',
        completedAt: new Date().toISOString(),
        sentCount: results.sent,
        failedCount: results.failed,
        processingErrors: results.errors
      });

      this.logger.info(`Campaign ${campaignId} completed:`, results);

      return results;

    } catch (error) {
      this.logger.error(`Error processing campaign ${campaignId}:`, error);

      // Mark campaign as failed
      await this.updateEmailCampaign(campaignId, {
        status: 'FAILED',
        completedAt: new Date().toISOString()
      });

      throw error;
    }
  }

  /**
   * Record an email event (open, click, bounce, etc.)
   * @param {Object} event
   * @param {string} event.emailMessageId - Email message ID
   * @param {string} event.userId - User ID
   * @param {string} event.clientId - Client ID
   * @param {string} event.eventType - OPEN | CLICK | BOUNCE | COMPLAINT | REPLY
   * @param {Object} event.metadata - Event-specific metadata
   */
  async recordEmailEvent({ userId, emailMessageId, clientId, campaignId, eventType, metadata = {}, timestamp }) {
    try {
      // Create EmailEvent record
      const response = await this.client.models.EmailEvent.create({
        userId,
        emailMessageId,
        clientId,
        campaignId,
        eventType,
        timestamp: timestamp || new Date().toISOString(),
        metadata: JSON.stringify(metadata),
        ipAddress: metadata.ipAddress,
        userAgent: metadata.userAgent,
        deviceType: metadata.deviceType,
        linkUrl: metadata.linkUrl,
        linkText: metadata.linkText,
        bounceType: metadata.bounceType,
        bounceSubType: metadata.bounceSubType
      });

      this.logger.info(`Recorded ${eventType} event for email ${emailMessageId}`);

      // Update EmailMessage counters
      if (eventType === 'OPEN') {
        await this.incrementEmailCounter(emailMessageId, 'openCount', metadata.isFirstOpen);
      } else if (eventType === 'CLICK') {
        await this.incrementEmailCounter(emailMessageId, 'clickCount', metadata.isFirstClick);
      } else if (eventType === 'BOUNCE') {
        await this.updateEmailMessage(emailMessageId, { status: 'BOUNCED' });
      }

      // Update campaign analytics if part of campaign
      if (campaignId) {
        await this.updateCampaignAnalytics(campaignId, eventType, metadata);
      }

      return response?.data;

    } catch (error) {
      this.logger.error('Error recording email event:', error);
      // Don't throw - event tracking failures shouldn't break the flow
      return null;
    }
  }

  /**
   * Get email messages for a user (inbox view)
   * @param {string} userId
   * @param {Object} options
   * @param {string} options.folder - inbox | sent | drafts | archive | trash
   * @param {number} options.limit - Max messages to return
   * @returns {Array} Email messages
   */
  async getEmailMessages(userId, { folder = 'inbox', limit = 50 } = {}) {
    try {
      const response = await this.client.models.EmailMessage.list({
        filter: { userId: { eq: userId } }
      });

      const allMessages = response?.data || [];

      // Filter by folder (inbox = INBOUND, sent = OUTBOUND)
      let filtered;
      if (folder === 'inbox') {
        filtered = allMessages.filter(msg => msg.direction === 'INBOUND');
      } else if (folder === 'sent') {
        filtered = allMessages.filter(msg => msg.direction === 'OUTBOUND');
      } else {
        filtered = allMessages.filter(msg => msg.folder === folder);
      }

      return filtered.slice(0, limit);

    } catch (error) {
      this.logger.error('Error fetching email messages:', error);
      return [];
    }
  }

  /**
   * Get email campaigns for a user
   * @param {string} userId
   * @param {number} limit
   * @returns {Array} Email campaigns
   */
  async getEmailCampaigns(userId, limit = 20) {
    try {
      const response = await this.client.graphql({
        query: `
          query ListEmailCampaigns($userId: ID!, $limit: Int) {
            listEmailCampaignsByUser(userId: $userId, limit: $limit, sortDirection: DESC) {
              items {
                id
                newsletterId
                name
                subject
                recipientCount
                status
                scheduledFor
                startedAt
                completedAt
                sentCount
                deliveredCount
                failedCount
                openedCount
                uniqueOpensCount
                clickedCount
                uniqueClicksCount
                bouncedCount
                repliedCount
                openRate
                clickRate
                clickToOpenRate
                bounceRate
                replyRate
                engagementScore
                createdAt
                updatedAt
              }
            }
          }
        `,
        variables: {
          userId,
          limit
        }
      });

      return response.data.listEmailCampaignsByUser.items || [];

    } catch (error) {
      this.logger.error('Error fetching email campaigns:', error);
      return [];
    }
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  /**
   * Create EmailMessage record in database
   * @private
   */
  async createEmailMessage(messageData) {
    try {
      const response = await this.client.models.EmailMessage.create({
        ...messageData,
        createdAt: new Date().toISOString()
      });

      return response?.data;

    } catch (error) {
      this.logger.error('Error creating email message:', error);
      throw error;
    }
  }

  /**
   * Update EmailMessage record
   * @private
   */
  async updateEmailMessage(messageId, updates) {
    try {
      const response = await this.client.models.EmailMessage.update({
        id: messageId,
        ...updates,
        updatedAt: new Date().toISOString()
      });

      return response?.data;

    } catch (error) {
      this.logger.error('Error updating email message:', error);
      throw error;
    }
  }

  /**
   * Create EmailCampaign record
   * @private
   */
  async createEmailCampaign(campaignData) {
    try {
      const response = await this.client.models.EmailCampaign.create({
        ...campaignData,
        createdAt: new Date().toISOString()
      });

      return response?.data;

    } catch (error) {
      this.logger.error('Error creating email campaign:', error);
      throw error;
    }
  }

  /**
   * Update EmailCampaign record
   * @private
   */
  async updateEmailCampaign(campaignId, updates) {
    try {
      const response = await this.client.models.EmailCampaign.update({
        id: campaignId,
        ...updates,
        updatedAt: new Date().toISOString()
      });

      return response?.data;

    } catch (error) {
      this.logger.error('Error updating email campaign:', error);
      throw error;
    }
  }

  /**
   * Update campaign progress during batch send
   * @private
   */
  async updateCampaignProgress(campaignId, progress) {
    try {
      await this.updateEmailCampaign(campaignId, {
        sentCount: progress.sentCount,
        failedCount: progress.failedCount,
        processingErrors: JSON.stringify(progress.processingErrors)
      });
    } catch (error) {
      this.logger.error('Error updating campaign progress:', error);
    }
  }

  /**
   * Update campaign analytics based on event
   * @private
   */
  async updateCampaignAnalytics(campaignId, eventType, metadata) {
    try {
      const updates = {};

      switch (eventType) {
        case 'DELIVERED':
          updates.deliveredCount = 1; // increment
          break;
        case 'OPEN':
          updates.openedCount = 1;
          if (metadata.isFirstOpen) {
            updates.uniqueOpensCount = 1;
          }
          break;
        case 'CLICK':
          updates.clickedCount = 1;
          if (metadata.isFirstClick) {
            updates.uniqueClicksCount = 1;
          }
          break;
        case 'BOUNCE':
          updates.bouncedCount = 1;
          break;
        case 'COMPLAINT':
          updates.complainedCount = 1;
          break;
        case 'REPLY':
          updates.repliedCount = 1;
          break;
      }

      if (Object.keys(updates).length > 0) {
        // Fetch current campaign stats
        const campaignResponse = await this.client.models.EmailCampaign.get({
          id: campaignId
        });

        const campaign = campaignResponse?.data;
        if (!campaign) {
          this.logger.warn(`Campaign ${campaignId} not found, skipping analytics update`);
          return;
        }

        // Calculate new counts by incrementing current values
        const newCounts = {
          id: campaignId,
          deliveredCount: (campaign.deliveredCount || 0) + (updates.deliveredCount || 0),
          openedCount: (campaign.openedCount || 0) + (updates.openedCount || 0),
          clickedCount: (campaign.clickedCount || 0) + (updates.clickedCount || 0),
          uniqueOpensCount: (campaign.uniqueOpensCount || 0) + (updates.uniqueOpensCount || 0),
          uniqueClicksCount: (campaign.uniqueClicksCount || 0) + (updates.uniqueClicksCount || 0),
          bouncedCount: (campaign.bouncedCount || 0) + (updates.bouncedCount || 0),
          complainedCount: (campaign.complainedCount || 0) + (updates.complainedCount || 0),
          repliedCount: (campaign.repliedCount || 0) + (updates.repliedCount || 0)
        };

        // Calculate rates (avoid division by zero)
        if (newCounts.deliveredCount > 0) {
          newCounts.openRate = (newCounts.openedCount / newCounts.deliveredCount) * 100;
          newCounts.clickRate = (newCounts.clickedCount / newCounts.deliveredCount) * 100;
        }

        // Update campaign in database
        await this.client.models.EmailCampaign.update(newCounts);

        this.logger.info(`Updated campaign ${campaignId} analytics:`, {
          eventType,
          newCounts: {
            delivered: newCounts.deliveredCount,
            opened: newCounts.openedCount,
            clicked: newCounts.clickedCount,
            openRate: newCounts.openRate?.toFixed(1) + '%',
            clickRate: newCounts.clickRate?.toFixed(1) + '%'
          }
        });
      }

    } catch (error) {
      this.logger.error('Error updating campaign analytics:', error);
      // Don't throw - analytics updates should not break email flow
    }
  }

  /**
   * Increment email message counter
   * @private
   */
  async incrementEmailCounter(messageId, counterName, isFirst = false) {
    try {
      const existing = await this.client.models.EmailMessage.get({ id: messageId });
      const current = existing?.data;
      if (!current) return;

      const updates = { id: messageId };
      if (counterName === 'openCount') {
        updates.openCount = (current.openCount || 0) + 1;
        if (isFirst) {
          updates.openedAt = new Date().toISOString();
        }
      } else if (counterName === 'clickCount') {
        updates.clickCount = (current.clickCount || 0) + 1;
      }

      await this.client.models.EmailMessage.update(updates);
    } catch (error) {
      this.logger.error('Error incrementing counter:', error);
    }
  }

  /**
   * Get client details
   * @private
   */
  async getClient(clientId) {
    try {
      const response = await this.client.models.Client.get({ id: clientId });
      return response?.data;

    } catch (error) {
      this.logger.error('Error fetching client:', error);
      return null;
    }
  }

  /**
   * Resolve the outbound sender identity for an advisor (#369).
   *
   * `from` MUST stay the SES-verified platform address (advisor domains are
   * not verified in SES) — the advisor's identity rides on fromName and
   * Reply-To so the email presents the advisor and replies reach them.
   * Falls back to the platform identity when the profile is unavailable.
   * @private
   */
  async getSenderIdentity(userId, preloadedProfile = undefined) {
    const from = import.meta.env.VITE_SES_FROM_EMAIL || 'hello@finnewsy.com';
    const platformName = import.meta.env.VITE_SES_FROM_NAME || 'Finnewsy';

    const profile =
      preloadedProfile !== undefined ? preloadedProfile : await this.getAdvisorProfile(userId);
    if (!profile) {
      return { from, fromName: platformName, replyTo: null };
    }

    const advisorName = [profile.firstName, profile.lastName].map(cleanName).filter(Boolean).join(' ');
    const firmName = this.resolveFirmName(profile);
    const fromName = [advisorName, firmName].filter(Boolean).join(', ') || platformName;

    return {
      from,
      fromName,
      // #433: only a platform-domain or SES-verified address may ride on Reply-To
      replyTo: resolveReplyTo({ profile })
    };
  }

  /**
   * Get sender email address for user
   * @private
   */
  async getSenderEmail(userId) {
    const identity = await this.getSenderIdentity(userId);
    return identity.from;
  }

  /**
   * Send email via AWS SES (Lambda function)
   * Network errors propagate to sendWithRetry for retry handling.
   * @private
   */
  async sendViaSES({ messageId, to, subject, bodyHtml, bodyText, from, fromName, replyTo, clientId, campaignId, inReplyToHeader, referencesHeader, enableTracking }) {
    // Use instance sesEndpoint (set via initialize()) or fall back to env var
    const lambdaUrl = this.sesEndpoint || import.meta.env.VITE_EMAIL_SENDER_URL;

    if (!lambdaUrl) {
      throw new Error('VITE_EMAIL_SENDER_URL not configured in .env.local');
    }

    this.logger.info('Sending via SES Lambda:', {
      messageId,
      to,
      subject,
      from,
      lambdaUrl: lambdaUrl.substring(0, 50) + '...'
    });

    // Prepare payload matching Lambda expected format
    const payload = {
      emailMessageId: messageId,
      to: Array.isArray(to) ? to : [to],
      from: from || import.meta.env.VITE_SES_FROM_EMAIL || 'hello@finnewsy.com', // Verified sender
      // #369: present the ADVISOR (name + firm), not the platform
      fromName: fromName || import.meta.env.VITE_SES_FROM_NAME || 'Finnewsy',
      // #369: client replies must reach the advisor, never a platform mailbox
      ...(replyTo ? { replyTo } : {}),
      subject: subject,
      bodyHtml: bodyHtml,
      // #368: block-aware conversion — the old tag-strip produced run-on
      // blobs in the text/plain part.
      bodyText: bodyText || htmlToEmailText(bodyHtml),
      // #391/#471: the Lambda skips its own pixel when tracking is off
      ...(enableTracking === false ? { enableTracking: false } : {}),
      // #362/#363: the Lambda uses clientId to token-sign the unsubscribe
      // link, emit List-Unsubscribe headers, and enforce suppression
      ...(clientId ? { clientId } : {}),
      // #363: campaignId enables the server-side approval re-check
      ...(campaignId ? { campaignId } : {}),
      // P2-A: AC3 - SMTP threading headers for email client thread grouping
      ...(inReplyToHeader ? { inReplyToHeader } : {}),
      ...(referencesHeader ? { referencesHeader } : {})
    };

    // #363: attach the caller's Cognito access token so the Lambda can
    // enforce auth (REQUIRE_AUTH). Omitted gracefully when no session —
    // enforcement is env-gated server-side, so unauthenticated dev flows
    // keep working until the flag flips. Memoized ~60s: a 500-recipient
    // campaign must not call fetchAuthSession per recipient per retry.
    const headers = { 'Content-Type': 'application/json' };
    try {
      const now = Date.now();
      if (!this._authHeaderCache || now - this._authHeaderCache.at > 60_000) {
        const { fetchAuthSession } = await import('aws-amplify/auth');
        const session = await fetchAuthSession();
        this._authHeaderCache = {
          token: session?.tokens?.accessToken?.toString() || null,
          at: now
        };
      }
      if (this._authHeaderCache.token) {
        headers.Authorization = `Bearer ${this._authHeaderCache.token}`;
      }
    } catch (authError) {
      this.logger.warn('No auth session for sender call (header omitted):', authError?.message);
    }

    // Call Lambda Function URL - network errors propagate for retry handling in sendWithRetry
    const response = await fetch(lambdaUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorText = await response.text();
      // Review fix: the Lambda's gate statuses (401 auth, 403 approval,
      // 422 banned terms) are DEFINITIVE — retrying the identical blocked
      // payload 3× just tripled Lambda calls and delayed the real error.
      if ([400, 401, 403, 404, 422].includes(response.status)) {
        let parsed = {};
        try { parsed = JSON.parse(errorText); } catch { /* non-JSON body */ }
        this.logger.warn(`Sender Lambda blocked send (${response.status}):`, parsed.error || errorText);
        return {
          success: false,
          sesMessageId: null,
          requestId: null,
          blocked: parsed.blocked === true,
          suppressed: parsed.suppressed === true,
          error: parsed.error || `Lambda returned ${response.status}`
        };
      }
      throw new Error(`Lambda returned ${response.status}: ${errorText}`);
    }

    const result = await response.json();

    // Lambda returns: { success: true, messageId: "...", emailMessageId: "..." }
    if (result.success) {
      this.logger.info('Email sent successfully:', result.messageId);
      return {
        success: true,
        sesMessageId: result.messageId,
        requestId: result.emailMessageId,
        error: null
      };
    } else {
      this.logger.error('Lambda returned failure:', result.error);
      return {
        success: false,
        sesMessageId: null,
        requestId: null,
        // Lambda-side suppression/compliance outcomes ride 200 bodies —
        // callers need the flags to classify (skipped vs failed).
        suppressed: result.suppressed === true,
        blocked: result.blocked === true,
        error: result.error || 'Unknown error from Lambda'
      };
    }
  }

  /**
   * Send email with retry logic
   * @private
   */
  async sendWithRetry({ messageId, to, subject, bodyHtml, bodyText, from, fromName, replyTo, clientId, campaignId, enableTracking }, maxRetries = 3) {
    let lastError = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        this.logger.info(`Send attempt ${attempt}/${maxRetries} for message ${messageId}`);

        const result = await this.sendViaSES({
          messageId,
          to,
          subject,
          bodyHtml,
          bodyText,
          from,
          fromName,
          replyTo,
          clientId,
          campaignId,
          enableTracking
        });

        // If successful, return immediately
        if (result.success) {
          this.logger.info(`Send succeeded on attempt ${attempt}`);
          return result;
        }

        // If SES returned a definitive failure (service-level), return immediately (don't retry)
        // Only retry on network errors / exceptions (caught below)
        if (result && !result.success) {
          this.logger.warn(`SES returned failure: ${result.error}`);
          return result;
        }

        lastError = result?.error;

        // Wait before retry (exponential backoff: 1s, 2s, 4s)
        if (attempt < maxRetries) {
          const delayMs = Math.pow(2, attempt - 1) * 1000;
          this.logger.info(`Retrying after ${delayMs}ms...`);
          await this.delay(delayMs);
        }

      } catch (error) {
        lastError = error.message;
        this.logger.error(`Attempt ${attempt} failed:`, error);

        if (attempt < maxRetries) {
          await this.delay(Math.pow(2, attempt - 1) * 1000);
        }
      }
    }

    // All retries failed
    this.logger.error(`All ${maxRetries} attempts failed for message ${messageId}`);
    return {
      success: false,
      sesMessageId: null,
      requestId: null,
      error: lastError
    };
  }

  /**
   * Check if error is non-retryable
   * @private
   */
  isNonRetryableError(error) {
    if (!error) return false;

    const nonRetryablePatterns = [
      'No recipients specified',
      'Invalid email',
      'Recipient not verified', // Sandbox mode
      'Suppression list',
      'Blacklisted',
      'Invalid sender'
    ];

    return nonRetryablePatterns.some(pattern =>
      error.toLowerCase().includes(pattern.toLowerCase())
    );
  }

  /**
   * Strip HTML tags for plain text fallback
   * @private
   */
  stripHtml(html) {
    if (!html) return '';
    return html
      .replace(/<style[^>]*>.*?<\/style>/gi, '')
      .replace(/<script[^>]*>.*?<\/script>/gi, '')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .trim();
  }

  /**
   * Personalize content by replacing placeholders
   * @private
   */
  personalizeContent(content, data) {
    if (!content) return content;

    let personalized = content;
    Object.keys(data).forEach(key => {
      const placeholder = new RegExp(`{{${key}}}`, 'g');
      personalized = personalized.replace(placeholder, data[key] || '');
    });

    return personalized;
  }

  /**
   * Add tracking pixel and wrap links for click tracking
   * @private
   */
  addTracking(html, { emailMessageId, clientId, newsletterId }) {
    if (!html) return html;

    let tracked = html;

    // Add tracking pixel before </body>
    const trackingUrl = this.trackingDomain || import.meta.env.VITE_EMAIL_TRACKER_URL;

    if (trackingUrl) {
      // Tracking pixel that calls Lambda when email is opened
      // Lambda expects '?id=emailMessageId' parameter
      const pixelUrl = emailMessageId ? `${trackingUrl}?id=${emailMessageId}&t=${Date.now()}` : `${trackingUrl}?t=${Date.now()}`;
      const trackingPixel = `<img src="${pixelUrl}" width="1" height="1" style="display:none;" alt="" />`;

      if (tracked.includes('</body>')) {
        tracked = tracked.replace('</body>', `${trackingPixel}</body>`);
      } else {
        tracked += trackingPixel;
      }
    }

    // Wrap links with click tracking
    const linkTrackerUrl = this.trackingDomain || import.meta.env.VITE_LINK_TRACKER_URL;

    if (linkTrackerUrl) {
      // Replace all <a href="..."> with tracking redirect
      tracked = tracked.replace(
        /<a\s+([^>]*?)href=["']([^"']+)["']([^>]*?)>/gi,
        (match, before, url, after) => {
          // Skip if already a tracking URL or special protocol
          if (url.includes('lambda-url.us-west-2.on.aws') || url.startsWith('mailto:') || url.startsWith('tel:')) {
            return match;
          }

          // Create tracking redirect URL
          const trackedUrl = `${linkTrackerUrl}?url=${encodeURIComponent(url)}&newsletter=${newsletterId}&client=${clientId}`;

          return `<a ${before}href="${trackedUrl}"${after}>`;
        }
      );
    }

    return tracked;
  }

  /**
   * Add unsubscribe link and CAN-SPAM footer to the email (#362).
   *
   * - Unsubscribe URL comes from configuration and uses the unsubscribe
   *   Lambda's expected `clientId` parameter. The email-sender Lambda appends
   *   the HMAC `token` at send time (the browser never holds the secret).
   * - CAN-SPAM §7704(a)(5) requires the sender's valid physical postal
   *   address in every commercial email — rendered from the advisor profile.
   * @private
   */
  addUnsubscribeLink(html, { clientId, newsletterId, advisorProfile = null }) {
    if (!html) return html;
    const unsubscribeFooter = this.buildFooterHtml({ clientId, newsletterId, advisorProfile });

    if (html.includes('</body>')) {
      return html.replace('</body>', `${unsubscribeFooter}</body>`);
    } else {
      return html + unsubscribeFooter;
    }
  }

  /**
   * The CAN-SPAM footer markup on its own (#362) — shared by the HTML path
   * (addUnsubscribeLink) and the text/plain path (sendNewsletter appends its
   * text rendering), so a recipient with HTML blocked still gets the
   * unsubscribe URL and postal address.
   * @private
   */
  buildFooterHtml({ clientId, newsletterId, advisorProfile = null }) {
    const base = this.getUnsubscribeBaseUrl();
    const sep = base.includes('?') ? '&' : '?';
    const unsubscribeUrl =
      `${base}${sep}clientId=${encodeURIComponent(clientId || '')}` +
      (newsletterId ? `&newsletterId=${encodeURIComponent(newsletterId)}` : '');

    const publicSiteUrl = import.meta.env.VITE_PUBLIC_SITE_URL || 'https://finnewsy.com';

    const firmName = this.resolveFirmName(advisorProfile);
    const addr = advisorProfile?.address || null;
    const postalAddress = addr
      ? [addr.street, addr.city, [addr.state, addr.zipCode].filter(Boolean).join(' ')]
          .filter((part) => part && String(part).trim())
          .join(', ')
      : null;

    const receivedBecauseLine = firmName
      ? `You received this email because you are a client of ${firmName}.`
      : 'You received this email because you are a client of our financial advisory services.';

    const identityLines = [
      firmName ? `<p style="margin: 0 0 4px 0;">${firmName}</p>` : '',
      postalAddress ? `<p style="margin: 0 0 10px 0;">${postalAddress}</p>` : ''
    ].join('');

    const unsubscribeFooter = `
    <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #6b7280; text-align: center; font-family: Arial, sans-serif;">
      <p style="margin: 0 0 10px 0;">${receivedBecauseLine}</p>
      ${identityLines}
      <p style="margin: 0 0 10px 0;">
        <a href="${unsubscribeUrl}" style="color: #6b7280; text-decoration: underline;">Unsubscribe</a> |
        <a href="${publicSiteUrl}/privacy" style="color: #6b7280; text-decoration: underline;">Privacy Policy</a> |
        <a href="${publicSiteUrl}/terms" style="color: #6b7280; text-decoration: underline;">Terms of Service</a>
      </p>
      <p style="margin: 10px 0 0 0; color: #9ca3af;">© ${new Date().getFullYear()} ${firmName || 'Finnewsy'}. All rights reserved.</p>
    </div>
  `;

    return unsubscribeFooter;
  }

  // ============================================================================
  // SCHEDULING METHODS (Issue #37)
  // ============================================================================

  /**
   * Schedule a campaign for future sending via EventBridge
   * @param {string} campaignId - The campaign ID
   * @param {string} scheduledFor - ISO date string for when to send
   * @returns {Promise<object>} - Schedule confirmation with ruleArn
   */
  async scheduleCampaign(campaignId, scheduledFor) {
    if (!campaignId) {
      throw new Error('campaignId is required');
    }
    if (!scheduledFor) {
      throw new Error('scheduledFor is required');
    }

    const scheduledTime = new Date(scheduledFor);
    const now = new Date();
    const minFutureMs = 5 * 60 * 1000; // 5 minutes

    if (scheduledTime <= now) {
      throw new Error('Scheduled time must be in the future');
    }

    if (scheduledTime - now < minFutureMs) {
      throw new Error('scheduledFor must be at least 5 minutes in the future');
    }

    try {
      this.logger.info(`[EmailService] Scheduling campaign ${campaignId} for ${scheduledFor}`);

      // Get the campaign to verify it exists
      const campaignResponse = await this.client.models.EmailCampaign.get({ id: campaignId });
      const campaign = campaignResponse?.data;
      if (!campaign) {
        throw new Error(`Campaign ${campaignId} not found`);
      }

      // TODO: Replace with actual Lambda call when EventBridge Lambda is deployed
      this.logger.info('[EmailService] EventBridge scheduling placeholder - campaign:', campaignId);
      const ruleArn = `placeholder-rule-${campaignId}`;

      // Update campaign status to SCHEDULED and store ruleArn
      await this.client.models.EmailCampaign.update({
        id: campaignId,
        status: 'SCHEDULED',
        scheduledFor,
        scheduleRuleArn: ruleArn,
        updatedAt: new Date().toISOString()
      });

      return {
        success: true,
        campaignId,
        scheduledFor,
        ruleArn
      };
    } catch (error) {
      // Re-throw validation errors directly
      if (
        error.message.includes('required') ||
        error.message.includes('future') ||
        error.message.includes('not found')
      ) {
        throw error;
      }
      this.logger.error('Error scheduling campaign:', error);
      throw error;
    }
  }

  /**
   * Cancel a scheduled campaign
   * @param {string} campaignId - The campaign ID
   * @returns {Promise<object>} - Cancellation confirmation
   */
  async cancelScheduledCampaign(campaignId) {
    try {
      this.logger.info(`[EmailService] Cancelling scheduled campaign ${campaignId}`);

      // Get the campaign
      const campaignResponse = await this.client.models.EmailCampaign.get({ id: campaignId });
      const campaign = campaignResponse?.data;

      if (!campaign) {
        throw new Error('Campaign not found');
      }

      if (campaign.status !== 'SCHEDULED') {
        throw new Error(`Campaign is not scheduled (current status: ${campaign.status})`);
      }

      // TODO: Replace with actual Lambda call when EventBridge Lambda is deployed
      // e.g., await this.deleteEventBridgeRule(campaign.scheduleRuleArn);
      this.logger.info('[EmailService] EventBridge cancellation placeholder - campaign:', campaignId);

      // Update campaign status back to DRAFT
      await this.client.models.EmailCampaign.update({
        id: campaignId,
        status: 'DRAFT',
        scheduledFor: null,
        scheduleRuleArn: null,
        updatedAt: new Date().toISOString()
      });

      return {
        success: true,
        campaignId
      };
    } catch (error) {
      if (error.message.includes('not found') || error.message.includes('not scheduled')) {
        throw error;
      }
      this.logger.error('Error cancelling scheduled campaign:', error);
      throw error;
    }
  }

  /**
   * Reschedule a campaign to a new time
   * @param {string} campaignId - The campaign ID
   * @param {string} newScheduledFor - New ISO date string
   * @returns {Promise<object>} - Reschedule confirmation
   */
  async rescheduleCampaign(campaignId, newScheduledFor) {
    // Validate new time first (throws if invalid)
    const newTime = new Date(newScheduledFor);
    const now = new Date();
    if (newTime <= now) {
      throw new Error('Scheduled time must be in the future');
    }

    // Cancel existing schedule
    await this.cancelScheduledCampaign(campaignId);

    // Schedule with new time
    const result = await this.scheduleCampaign(campaignId, newScheduledFor);

    return result;
  }

  /**
   * Get all campaigns for calendar display (scheduled, sent, completed)
   * @param {string} startDate - ISO date string for range start
   * @param {string} endDate - ISO date string for range end
   * @returns {Promise<Array>} - Array of { id, name, scheduledFor, recipientCount, status }
   */
  async getScheduledCampaigns(startDate, endDate) {
    try {
      const response = await this.client.models.EmailCampaign.list({
        filter: {
          and: [
            { scheduledFor: { ge: startDate } },
            { scheduledFor: { le: endDate } }
          ]
        }
      });

      const campaigns = response?.data || [];

      return campaigns.map(c => ({
        id: c.id,
        name: c.name,
        scheduledFor: c.scheduledFor,
        recipientCount: c.recipientCount,
        status: c.status
      }));
    } catch (error) {
      this.logger.error('Error fetching campaigns for calendar:', error);
      return [];
    }
  }

  /**
   * Delay helper for rate limiting
   * @private
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Export class for testing
export { EmailService };

// Export singleton instance as default
export default new EmailService();
